import { useEffect, useState } from "react";
import StarRating from "@/components/feature/StarRating";
import { fetchSellerReviews, summarizeRatings, type ProductReview } from "@/lib/reviews";

interface SellerReviewsListProps {
  sellerId: string;
  /** Nombre de la persona valorada, para los textos. */
  sellerName?: string;
}

function reviewDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("es-DO", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

/** Lista de reseñas recibidas por un vendedor, con su resumen de estrellas. */
export default function SellerReviewsList({
  sellerId,
  sellerName,
}: SellerReviewsListProps) {
  const [reviews, setReviews] = useState<ProductReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);
    fetchSellerReviews(sellerId)
      .then((data) => {
        if (mounted) setReviews(data);
      })
      .catch((e) => {
        if (mounted) {
          setError(
            e instanceof Error ? e.message : "No se pudieron cargar las valoraciones.",
          );
        }
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [sellerId]);

  const summary = summarizeRatings(reviews);
  const who = sellerName ?? "este vendedor";

  return (
    <section className="mt-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-heading text-lg font-bold text-foreground-950">
          Valoraciones ({summary.count})
        </h2>
        {summary.count > 0 && (
          <StarRating
            value={summary.average}
            count={summary.count}
            size="md"
            showValue
          />
        )}
      </div>

      {loading ? (
        <div className="mt-5 flex items-center justify-center py-10">
          <i className="ri-loader-4-line animate-spin text-2xl text-foreground-400"></i>
        </div>
      ) : error ? (
        <p className="mt-4 text-sm text-foreground-500">{error}</p>
      ) : reviews.length === 0 ? (
        <div className="mt-4 flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-background-300 bg-background-50 py-12 text-center">
          <i className="ri-star-line text-4xl text-foreground-300"></i>
          <p className="text-sm text-foreground-500">
            Todavía no hay valoraciones para {who}.
          </p>
        </div>
      ) : (
        <ul className="mt-4 flex flex-col divide-y divide-background-200/70">
          {reviews.map((review) => (
            <li key={review.id} className="flex gap-3 py-4">
              {review.reviewer_avatar ? (
                <img
                  src={review.reviewer_avatar}
                  alt={review.reviewer_name}
                  className="h-9 w-9 shrink-0 rounded-full border border-background-200 object-cover object-top"
                />
              ) : (
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary-500 text-sm font-semibold text-white">
                  {review.reviewer_name.charAt(0).toUpperCase()}
                </span>
              )}
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span className="text-sm font-semibold text-foreground-950">
                    {review.reviewer_name}
                  </span>
                  <StarRating value={review.rating} size="sm" />
                  <span className="text-xs text-foreground-400">
                    {reviewDate(review.created_at)}
                  </span>
                </div>
                {review.comment && (
                  <p className="mt-1 whitespace-pre-line break-words text-sm leading-relaxed text-foreground-600">
                    {review.comment}
                  </p>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}